import { type ReactNode, useEffect, useState } from 'react';
import { ChevronDown, LogOut, Menu, X } from 'lucide-react';
import { cx } from './form.tsx';
import logoUrl from './loyaltyplus-logo.png';

export function Logo({ className, subtitle }: { className?: string; subtitle?: string }) {
  return (
    <div className={cx('flex items-center gap-2.5', className)}>
      <img src={logoUrl} alt="" className="h-7 w-auto" />
      <div className="leading-tight">
        <div className="text-[17px] font-semibold tracking-tight text-ink">
          Loyalty<span className="text-brand-600">Plus</span>
        </div>
        {subtitle && <div className="text-[11px] uppercase tracking-wider text-muted">{subtitle}</div>}
      </div>
    </div>
  );
}

export interface NavItem {
  to: string;
  label: string;
  icon?: ReactNode;
  /** Section heading shown above this item. */
  section?: string;
}

export interface AppShellProps {
  subtitle: string;
  nav: NavItem[];
  /** The page shown now; its section stays open. */
  currentPath: string;
  /** Render one nav link. Apps pass their router's NavLink so active state works. */
  renderLink: (item: NavItem, className: (active: boolean) => string, onNavigate: () => void) => ReactNode;
  user: { name: string; email: string } | null;
  onLogout: () => void;
  /** Shown in the top bar, e.g. the brand name in the brand portal. */
  context?: ReactNode;
  children: ReactNode;
}

interface NavGroup {
  section: string | null;
  items: NavItem[];
}

function groupNav(nav: NavItem[]): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const item of nav) {
    if (item.section || !groups.length) groups.push({ section: item.section ?? null, items: [] });
    groups[groups.length - 1]!.items.push(item);
  }
  return groups;
}

const isCurrent = (to: string, path: string) => path === to || (to !== '/' && path.startsWith(`${to}/`));

/** Sections the user opened in the sidebar, remembered on this device. */
function useOpenSections(key: string): [Set<string>, (section: string) => void] {
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(key) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify([...open]));
    } catch {
      // private mode: the sidebar just starts collapsed next time
    }
  }, [key, open]);
  const toggle = (section: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  return [open, toggle];
}

/**
 * Sidebar + top bar. The sidebar collapses into a drawer under 1024px. Sections with more than one page start
 * collapsed; the section of the page shown now is always open.
 */
export function AppShell({ subtitle, nav, currentPath, renderLink, user, onLogout, context, children }: AppShellProps) {
  const [open, setOpen] = useState(false);
  const [openSections, toggleSection] = useOpenSections(`lp.nav.open.${subtitle}`);
  const linkClass = (active: boolean) =>
    cx(
      'flex items-center gap-3 rounded-full px-4 py-2 text-[15px] transition-colors',
      active ? 'bg-brand/15 font-medium text-ink' : 'text-ink-soft hover:bg-mint',
    );
  const sidebar = (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto px-3 py-5">
      <div className="mb-5 px-3">
        <Logo subtitle={subtitle} />
      </div>
      {groupNav(nav).map(({ section, items }, i) => {
        const links = items.map((item) => <div key={item.to}>{renderLink(item, linkClass, () => setOpen(false))}</div>);
        if (!section || items.length < 2) {
          return (
            <div key={section ?? i}>
              {section && <div className="mb-1 mt-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-muted">{section}</div>}
              {links}
            </div>
          );
        }
        const here = items.some((item) => isCurrent(item.to, currentPath));
        const expanded = here || openSections.has(section);
        const id = `nav-section-${i}`;
        return (
          <div key={section}>
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={id}
              disabled={here}
              onClick={() => toggleSection(section)}
              className="mb-1 mt-4 flex w-full items-center justify-between rounded-full px-4 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted transition-colors enabled:hover:bg-mint enabled:hover:text-ink"
            >
              <span>{section}</span>
              <span className="flex items-center gap-1.5 normal-case tracking-normal">
                {!expanded && <span className="font-normal">{items.length}</span>}
                <ChevronDown className={cx('size-3.5 transition-transform', expanded && 'rotate-180')} aria-hidden />
              </span>
            </button>
            {expanded && (
              <div id={id} className="flex flex-col gap-1">
                {links}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
  return (
    <div className="min-h-screen bg-canvas">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-line bg-white lg:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-ink/30" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button type="button" aria-label="Close menu" className="absolute right-3 top-4 rounded-full p-2 hover:bg-mint" onClick={() => setOpen(false)}>
              <X className="size-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line bg-white/90 px-4 backdrop-blur sm:px-6">
          <button type="button" aria-label="Open menu" className="rounded-full p-2 hover:bg-mint lg:hidden" onClick={() => setOpen(true)}>
            <Menu className="size-5 text-ink" />
          </button>
          <div className="min-w-0 flex-1 truncate">{context}</div>
          {user && (
            <div className="flex items-center gap-3">
              <div className="hidden text-right leading-tight sm:block">
                <div className="text-sm font-medium text-ink">{user.name}</div>
                <div className="text-xs text-muted">{user.email}</div>
              </div>
              <button type="button" onClick={onLogout} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-ink-soft hover:bg-mint">
                <LogOut className="size-4" />
                <span className="hidden sm:inline">登出</span>
              </button>
            </div>
          )}
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">{children}</main>
      </div>
    </div>
  );
}

/** Centred card for login, invite and bootstrap pages. */
export function AuthLayout({ subtitle, title, children }: { subtitle: string; title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-mint px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo subtitle={subtitle} />
        </div>
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-line">
          <h1 className="mb-6 text-2xl font-medium uppercase tracking-tight text-ink">{title}</h1>
          {children}
        </div>
        <p className="mt-6 text-center text-xs text-muted">© {new Date().getFullYear()} Sampras · LoyaltyPlus</p>
      </div>
    </div>
  );
}

export function PageHeader({ title, description, actions, back }: { title: string; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {back && <div className="mb-2">{back}</div>}
        <h1 className="text-2xl font-medium uppercase tracking-tight text-ink sm:text-[28px]">{title}</h1>
        {description && <p className="mt-1 text-[15px] text-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, description, actions, children, className, padded = true }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; padded?: boolean }) {
  return (
    <section className={cx('rounded-2xl bg-white ring-1 ring-line', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4 sm:px-6">
          <div>
            {title && <h2 className="text-[17px] font-medium text-ink">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className={padded ? 'p-5 sm:p-6' : undefined}>{children}</div>
    </section>
  );
}

/** Big number tile for dashboards. */
export function Stat({ label, value, hint, tone = 'brand' }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'brand' | 'teal' | 'coral' | 'amber' | 'sky' }) {
  const bar = { brand: 'bg-brand', teal: 'bg-teal', coral: 'bg-coral', amber: 'bg-amber', sky: 'bg-sky' }[tone];
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-5 ring-1 ring-line">
      <span className={cx('absolute inset-y-0 left-0 w-1', bar)} />
      <div className="text-sm text-muted">{label}</div>
      <div className="mt-1 text-[28px] font-medium tracking-tight text-ink">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}
